import { useCallback } from 'react'
import { useStyles } from './styles'
import {
  Button,
  ConexaLogo,
  Text,
  getTheme,
  useMediaQuery,
} from '@conexasaude/conexa-hero'

import { BackStepper } from '../BackStepper'
import { MainLayoutProps } from './types'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { MobileHeader } from '../MobileHeader'
import { RenderIf } from '@/components/RenderIf'
import { Illustration } from '@/components/HideIllustrationsExternal'
import { useParams } from 'react-router-dom'
import { SEND_TO } from '@/modules/Signup/constants'
import CustomTimezone from '@/components/CustomTimezone'

export const MainLayout: React.FunctionComponent<MainLayoutProps> = props => {
  const {
    children,
    disabledNextButton,
    actionNextButton,
    variantNextButton,
    variantLinkButton,
    isLoading,
    labelNextButton,
    labelLinkButton,
    actionLinkButton,
    actionBackStepper,
    showSupportButton,
    hideMobileHeader,
    title,
    subtitle,
    showLogo,
    bodySize,
    showfooterCopyrightText = true,
    tagEventName,
    additionalFooterSpacing = false,
  } = props
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm})`)
  const isTablet = useMediaQuery(`(min-width: ${theme.grid.breakpoint.lg})`)

  const styles = useStyles({ bodySize })

  const { logAnalyticsEvent } = useFirebaseContext()
  const params = useParams<{ clinic?: string }>()

  const handleBackStepper = useCallback(() => {
    logAnalyticsEvent('at_patient_activation', {
      interaction: 'back_click',
      options: {
        sendTo: SEND_TO,
      },
    })
    actionBackStepper()
  }, [actionBackStepper, logAnalyticsEvent])

  return (
    <div css={styles.layoutContainer}>
      <RenderIf condition={!isMobile}>
        <div css={styles.containerBackground}>
          <div css={styles.backgroundContentContainer}>
            <div css={styles.backgroundNoise} />
            <Illustration
              name="system"
              css={styles.backgroundContainerIcon}
              width={isTablet ? '560px' : '80%'}
            />
            <Text
              variant="headingXLarge"
              color={theme.colors.background.white}
              css={styles.backgroundContainerTitle}
            >
              Olá, nós somos a Conexa Saúde
            </Text>
            <Text
              variant="headingMedium"
              color={theme.colors.background.white}
              css={styles.backgroundContainerSubtitle}
            >
              Seu novo jeito de cuidar da saúde
            </Text>
          </div>
        </div>
      </RenderIf>
      <RenderIf condition={isMobile && !hideMobileHeader}>
        <MobileHeader />
      </RenderIf>
      <div css={styles.body}>
        <div css={styles.contentWrapper}>
          <RenderIf condition={showLogo}>
            <div css={styles.logoWrapper}>
              <RenderIf condition={!!params.clinic && showLogo && !isMobile}>
                <img
                  src={`${process.env.VITE_APP_LINK_LOGO_AWS}${params.clinic}.png`}
                  alt="logo"
                  css={styles.logoWhiteLabel}
                />
              </RenderIf>
              <RenderIf condition={!params.clinic && showLogo && !isMobile}>
                <ConexaLogo
                  base="colorful"
                  inverse
                  width={150}
                  height={32.35}
                />
              </RenderIf>
            </div>
          </RenderIf>
          <div css={styles.mobileStepperArea}>
            <RenderIf condition={!!actionBackStepper && isMobile}>
              <BackStepper
                onActionClick={handleBackStepper}
                color={theme.colors.neutral[200]}
              />
            </RenderIf>
          </div>
          <div css={styles.flexContainer}>
            <div css={styles.stepperArea}>
              <RenderIf condition={!!actionBackStepper && !isMobile}>
                <BackStepper
                  onActionClick={handleBackStepper}
                  color={theme.colors.neutral[200]}
                />
              </RenderIf>
            </div>
            <div css={styles.mainCard}>
              <div css={styles.contentContainer}>
                <div css={styles.content}>
                  <RenderIf condition={!!title}>
                    <div css={styles.titleContainer}>
                      <h2 dangerouslySetInnerHTML={{ __html: title }} />
                    </div>
                  </RenderIf>
                  <RenderIf condition={!!subtitle}>
                    <p
                      css={styles.subtitleWrapper}
                      dangerouslySetInnerHTML={{ __html: subtitle }}
                    />
                  </RenderIf>

                  <div css={styles.childrenContainer}>{children}</div>
                  <div css={styles.buttonWrapper}>
                    <RenderIf condition={!!actionNextButton}>
                      <Button
                        disabled={disabledNextButton}
                        onClick={actionNextButton}
                        variant={variantNextButton}
                        busy={isLoading}
                        fullWidth
                      >
                        {labelNextButton}
                      </Button>
                      <RenderIf condition={!!labelLinkButton}>
                        <Button
                          variant={variantLinkButton || 'secondary'}
                          fullWidth
                          onClick={actionLinkButton}
                        >
                          {labelLinkButton}
                        </Button>
                      </RenderIf>
                    </RenderIf>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CustomTimezone
          showSupportButton={showSupportButton}
          tagEventName={tagEventName}
          tagInteraction="need_help_click"
          showfooterCopyrightText={showfooterCopyrightText}
          additionalFooterSpacing={
            (!!actionBackStepper && !isMobile) || additionalFooterSpacing
          }
        />
      </div>
    </div>
  )
}
